import { useState, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useAuth } from '../../../hooks/useAuth'
import { getOrCreateLifesaverForm, updateLifesaverForm } from '../../../services/lifesaverLinkService'
import { showAlert } from '../../../services/dialogService'
import InfoButton from '../../../components/InfoButton/InfoButton'
import Loader from '../../../components/Loader/Loader'
import '../Section.css'
import './TheLifesaverLink.css'

function TheLifesaverLink() {
  const { user, loading: authLoading } = useAuth()
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [localForm, setLocalForm] = useState(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!user) return

    const loadForm = async () => {
      try {
        setLoading(true)
        const formData = await getOrCreateLifesaverForm(user.id)
        
        // IMPORTANTE: Cargar los datos EXACTAMENTE como están guardados en la base de datos
        // Solo agregar campos requeridos si realmente no existen (no sobrescribir campos existentes)
        const fields = formData.form_fields || []
        const requiredFields = ['first_name', 'last_name', 'email']
        const existingRequired = fields.filter(f => requiredFields.includes(f.name))
        const otherFields = fields.filter(f => !requiredFields.includes(f.name))
        
        // Verificar si faltan campos requeridos
        const missingRequired = requiredFields.filter(
          name => !existingRequired.some(f => f.name === name)
        )
        
        // Solo actualizar si realmente faltan campos requeridos
        if (missingRequired.length > 0) {
          const fieldConfigs = {
            first_name: { name: 'first_name', label: 'First Name', required: true, type: 'text' },
            last_name: { name: 'last_name', label: 'Last Name', required: true, type: 'text' },
            email: { name: 'email', label: 'Email', required: true, type: 'email' },
          }
          
          const newRequiredFields = missingRequired.map(name => fieldConfigs[name])
          // IMPORTANTE: Mantener el orden EXACTO de los campos existentes
          // Solo agregar los campos requeridos que faltan al principio, pero mantener el orden del resto
          // Esto preserva el orden que el usuario configuró
          const updatedFields = [...newRequiredFields, ...fields]
          
          // Actualizar en la base de datos solo si faltan campos requeridos
          await updateLifesaverForm(formData.id, { form_fields: updatedFields })
          formData.form_fields = updatedFields
        }
        
        // IMPORTANTE: Usar los datos exactos de la base de datos, sin modificar
        // Esto asegura que los cambios guardados por el usuario se muestren correctamente
        setForm(formData)
        setLocalForm(formData)
        setHasChanges(false)
      } catch (error) {
        console.error('Error loading form:', error)
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        })
      } finally {
        // Pequeño delay para evitar parpadeo
        setTimeout(() => {
          setLoading(false)
        }, 150)
      }
    }

    loadForm()
  }, [user])

  const handleToggle = (field) => {
    if (!localForm) return

    const newValue = !localForm[field]
    setLocalForm({
      ...localForm,
      [field]: newValue,
    })
    setHasChanges(true)
  }

  const handleSaveChanges = async (formToSave = null) => {
    console.log('🔴 handleSaveChanges - FUNCIÓN LLAMADA')
    console.log('🔴 formToSave:', formToSave)
    console.log('🔴 localForm:', localForm)
    console.log('🔴 form:', form)
    
    const formToUpdate = formToSave || localForm
    console.log('🔴 formToUpdate:', formToUpdate)
    
    if (!formToUpdate || !form) {
      console.error('❌ handleSaveChanges - SALIENDO TEMPRANO: formToUpdate o form es null/undefined')
      console.error('❌ formToUpdate:', formToUpdate)
      console.error('❌ form:', form)
      return
    }

    try {
      console.log('🔴 handleSaveChanges - INICIANDO TRY BLOCK')
      setSaving(true)
      const updates = {
        enabled: formToUpdate.enabled,
        collect_household: formToUpdate.collect_household,
        form_fields: formToUpdate.form_fields,
      }
      
      // Logging para diagnóstico (siempre mostrar)
      console.log('🟢 handleSaveChanges - INICIANDO GUARDADO')
      console.log('🟢 form.id:', form.id)
      console.log('🟢 updates:', JSON.stringify(updates, null, 2))
      console.log('🟢 formToUpdate.form_fields length:', formToUpdate.form_fields?.length)
      console.log('🟢 current form.form_fields length:', form.form_fields?.length)
      console.log('🟢 formToUpdate.enabled:', formToUpdate.enabled)
      console.log('🟢 formToUpdate.collect_household:', formToUpdate.collect_household)
      
      const updated = await updateLifesaverForm(form.id, updates)
      
      console.log('🟢 handleSaveChanges - updated form recibido:')
      console.log('🟢 updated.form_fields length:', updated.form_fields?.length)
      console.log('🟢 updated.enabled:', updated.enabled)
      console.log('🟢 updated.collect_household:', updated.collect_household)
      
      setForm(updated)
      setLocalForm(updated)
      setHasChanges(false)
      if (!formToSave) {
        await showAlert('Changes saved successfully!')
      }
    } catch (error) {
      console.error('Error saving changes:', error)
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
      })
      
      if (!formToSave) {
        // Mostrar mensaje de error más específico
        let errorMessage = 'Error saving changes. Please try again.'
        
        // Verificar si es un error 406 (Not Acceptable)
        if (error.code === 'PGRST116' || error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          errorMessage = 'Error 406: Could not update form. This usually means the form was not found or there was a database error. Please:\n\n1. Refresh the page\n2. Try saving again\n\nIf the problem persists, the code may need to be updated on the server.'
        } else if (error.message) {
          errorMessage = error.message
        } else if (error.code === '42501') {
          errorMessage = 'Permission denied. Please check your account permissions.'
        }
        
        await showAlert(errorMessage)
        // NO resetear hasChanges si hay error, para que el usuario pueda intentar de nuevo
        setHasChanges(true) // Mantener el estado de cambios para que pueda intentar de nuevo
      }
    } finally {
      setSaving(false)
    }
  }

  const handleToggleField = (fieldName) => {
    if (!localForm) return

    // No permitir desactivar first_name, last_name o email (campos esenciales)
    if (fieldName === 'first_name' || fieldName === 'last_name' || fieldName === 'email') {
      return // No hacer nada, estos campos siempre deben estar habilitados
    }

    const currentFields = localForm.form_fields || []
    const fieldIndex = currentFields.findIndex((f) => f.name === fieldName)

    let newFields
    if (fieldIndex >= 0) {
      // Si el campo existe, eliminarlo
      newFields = currentFields.filter((f) => f.name !== fieldName)
    } else {
      // Si no existe, agregarlo con configuración por defecto
      const fieldConfigs = {
        phone: { name: 'phone', label: 'Phone Number', required: false, type: 'tel' },
        country: { name: 'country', label: 'Country', required: false, type: 'text' },
        address: { name: 'address', label: 'Address', required: false, type: 'textarea' },
        plus_one: { name: 'plus_one', label: 'Plus One', required: false, type: 'checkbox' },
      }

      const newField = fieldConfigs[fieldName]
      if (newField) {
        // Mantener first_name, last_name y email siempre al principio y asegurarse de que existan
        const requiredFieldConfigs = {
          first_name: { name: 'first_name', label: 'First Name', required: true, type: 'text' },
          last_name: { name: 'last_name', label: 'Last Name', required: true, type: 'text' },
          email: { name: 'email', label: 'Email', required: true, type: 'email' },
        }
        
        // Asegurar que los campos requeridos estén presentes
        const requiredFields = []
        if (!currentFields.some(f => f.name === 'first_name')) {
          requiredFields.push(requiredFieldConfigs.first_name)
        }
        if (!currentFields.some(f => f.name === 'last_name')) {
          requiredFields.push(requiredFieldConfigs.last_name)
        }
        if (!currentFields.some(f => f.name === 'email')) {
          requiredFields.push(requiredFieldConfigs.email)
        }
        
        // Agregar los campos requeridos existentes
        const existingRequired = currentFields.filter((f) => 
          f.name === 'first_name' || f.name === 'last_name' || f.name === 'email'
        )
        const otherFields = currentFields.filter((f) => 
          f.name !== 'first_name' && f.name !== 'last_name' && f.name !== 'email'
        )
        newFields = [...requiredFields, ...existingRequired, ...otherFields, newField]
      } else {
        newFields = [...currentFields]
      }
    }
    
    // Asegurar que first_name, last_name y email siempre estén presentes
    const requiredNames = ['first_name', 'last_name', 'email']
    const requiredFieldConfigs = {
      first_name: { name: 'first_name', label: 'First Name', required: true, type: 'text' },
      last_name: { name: 'last_name', label: 'Last Name', required: true, type: 'text' },
      email: { name: 'email', label: 'Email', required: true, type: 'email' },
    }
    
    const hasAllRequired = requiredNames.every(name => 
      newFields.some(f => f.name === name)
    )
    
    if (!hasAllRequired) {
      // Agregar los campos requeridos que faltan
      requiredNames.forEach(name => {
        if (!newFields.some(f => f.name === name)) {
          newFields.unshift(requiredFieldConfigs[name])
        }
      })
    }

    const updatedForm = {
      ...localForm,
      form_fields: newFields,
    }
    setLocalForm(updatedForm)
    setHasChanges(true)
    
    // NO guardar automáticamente - dejar que el usuario guarde manualmente con el botón
    // Esto evita problemas si el guardado falla y permite que el usuario vea el botón "Save Changes"
  }

  const isFieldEnabled = (fieldName) => {
    if (!localForm?.form_fields) {
      // Si no hay form_fields, los campos requeridos siempre están habilitados
      return fieldName === 'first_name' || fieldName === 'last_name' || fieldName === 'email'
    }
    return localForm.form_fields.some((f) => f.name === fieldName)
  }

  const handleCopyLink = () => {
    if (!form) return

    const formUrl = `${window.location.origin}/lifesaver-link/${form.form_slug}`
    navigator.clipboard.writeText(formUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handlePreview = () => {
    if (!form) return
    const formUrl = `${window.location.origin}/lifesaver-link/${form.form_slug}`
    window.open(formUrl, '_blank')
  }

  // Mostrar loader mientras se cargan los datos o mientras se verifica la autenticación
  if (authLoading || loading) {
    return <Loader />
  }

  if (!form && !loading) {
    return (
      <div className="dashboard-section">
        <div className="lifesaver-error">
          <h3>Error loading form</h3>
          <p>Please check the browser console for more details.</p>
          <p>Common issues:</p>
          <ul>
            <li>The guest_forms table may not exist in your database</li>
            <li>RLS policies may need to be configured</li>
            <li>You may need to create a wedding first</li>
          </ul>
        </div>
      </div>
    )
  }

  if (!localForm) {
    return <Loader />
  }

  const formUrl = localForm ? `${window.location.origin}/lifesaver-link/${localForm.form_slug}` : ''

  return (
    <div className="dashboard-section lifesaver-config">
      <h1>
        GUEST MANAGER - THE LIFESAVER LINK
        <InfoButton
          title="The Lifesaver Link"
          content={
            <div>
              <p>
                Share a simple link with your guests so they can add themselves to your guest list. 
                No more manual entry — guests fill in their own details, and you get everything organized automatically.
              </p>
              <p>
                <strong>How it works:</strong>
              </p>
              <ul>
                <li>Generate a unique link for your wedding</li>
                <li>Share via SMS, WhatsApp, or email</li>
                <li>Guests add their information directly</li>
                <li>All details appear in your Guest Manager automatically</li>
                <li>Option to collect household information (plus-ones, family groups)</li>
              </ul>
            </div>
          }
        />
      </h1>

      <div className="lifesaver-content">
        {/* Section 1: Your Contact Collector */}
        <div className="lifesaver-panel lifesaver-collector-section">
          <div className="lifesaver-section-header">
            <h2>Your Contact Collector</h2>
            {localForm?.enabled && (
              <span className="lifesaver-status-badge active">Active</span>
            )}
          </div>
          <p className="lifesaver-description">
            This is your unique and permanent form. Share this link with all your guests. You can modify the fields or deactivate it whenever you want.
          </p>
          
          {/* Upload Spreadsheet Section - Temporarily disabled */}
          {/* <div className="lifesaver-spreadsheet-section">
            <h3>Already have a list of contacts?</h3>
            <p>Get a head start by uploading a spreadsheet of your contacts.</p>
            <button className="lifesaver-link-btn">Upload Your Spreadsheet</button>
          </div> */}
        </div>

        {/* Section 2: Form Fields */}
        <div className="lifesaver-panel lifesaver-fields-panel">
          <h2>Form Fields</h2>
          <p className="lifesaver-fields-hint">
            Choose what information you want to collect. You can modify this at any time.
          </p>
          <div className="lifesaver-fields-grid">
            <div className="lifesaver-field-card lifesaver-field-required">
              <label className="lifesaver-field-checkbox">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  readOnly
                />
                <span className="lifesaver-field-name">First Name</span>
                <span className="lifesaver-field-required-text">(required)</span>
              </label>
            </div>
            <div className="lifesaver-field-card lifesaver-field-required">
              <label className="lifesaver-field-checkbox">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  readOnly
                />
                <span className="lifesaver-field-name">Last Name</span>
                <span className="lifesaver-field-required-text">(required)</span>
              </label>
            </div>
            <div className="lifesaver-field-card lifesaver-field-required">
              <label className="lifesaver-field-checkbox">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  readOnly
                />
                <span className="lifesaver-field-name">Email</span>
                <span className="lifesaver-field-required-text">(required)</span>
              </label>
            </div>
            <div className="lifesaver-field-card">
              <label className="lifesaver-field-checkbox">
                <input
                  type="checkbox"
                  checked={isFieldEnabled('phone')}
                  onChange={() => handleToggleField('phone')}
                />
                <span className="lifesaver-field-name">Phone</span>
              </label>
            </div>
            <div className="lifesaver-field-card">
              <label className="lifesaver-field-checkbox">
                <input
                  type="checkbox"
                  checked={isFieldEnabled('country')}
                  onChange={() => handleToggleField('country')}
                />
                <span className="lifesaver-field-name">Country</span>
              </label>
            </div>
            {localForm && localForm.collect_household && (
              <div className="lifesaver-field-card">
                <label className="lifesaver-field-checkbox">
                  <input
                    type="checkbox"
                    checked={isFieldEnabled('address')}
                    onChange={() => handleToggleField('address')}
                  />
                  <span className="lifesaver-field-name">Address</span>
                </label>
              </div>
            )}
            <div className="lifesaver-field-card">
              <label className="lifesaver-field-checkbox">
                <input
                  type="checkbox"
                  checked={isFieldEnabled('plus_one')}
                  onChange={() => handleToggleField('plus_one')}
                />
                <span className="lifesaver-field-name">Plus One</span>
              </label>
            </div>
          </div>
        </div>

        {/* Section 3: Form Status */}
        <div className="lifesaver-panel lifesaver-status-panel">
          <div className="lifesaver-section-header">
            <h2>Form Status</h2>
            {localForm?.enabled && (
              <span className="lifesaver-status-badge-large active">ACTIVE</span>
            )}
          </div>
          
          <div className={`lifesaver-status-message ${localForm?.enabled ? 'active' : 'inactive'}`}>
            <span className="lifesaver-status-icon">{localForm?.enabled ? '✓' : '✗'}</span>
            <span>
              {localForm?.enabled 
                ? "The form is active. Guests can access the link and submit their data."
                : "The form is inactive. Guests cannot access the link."}
            </span>
          </div>

          <div className="lifesaver-status-controls">
            <div className="lifesaver-status-toggle">
              <div className="lifesaver-toggle-label">
                <span>Enable Contact Collector</span>
                <span className="lifesaver-toggle-hint">
                  {localForm?.enabled ? 'Form is active' : 'Form is inactive'}
                </span>
              </div>
              <label className="lifesaver-switch" style={{ display: 'inline-block', visibility: 'visible', width: '50px', height: '24px', position: 'relative' }}>
                <input
                  type="checkbox"
                  checked={localForm?.enabled || false}
                  onChange={() => handleToggle('enabled')}
                  disabled={!localForm}
                  style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                />
                <span className={`lifesaver-slider ${localForm?.enabled ? 'active' : ''}`} style={{ display: 'block', visibility: 'visible' }}></span>
              </label>
            </div>

            <div className="lifesaver-status-toggle">
              <div className="lifesaver-toggle-label">
                <span>Enable Collect Household</span>
                <span className="lifesaver-help-icon" title="Collect physical addresses for sending invitations">?</span>
              </div>
              <label className="lifesaver-switch">
                <input
                  type="checkbox"
                  checked={localForm?.collect_household || false}
                  onChange={() => handleToggle('collect_household')}
                  disabled={!localForm}
                />
                <span className={`lifesaver-slider ${localForm?.collect_household ? 'active' : ''}`}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Section 4: Your Magic Link */}
        <div className="lifesaver-panel lifesaver-link-panel">
          <div className="lifesaver-section-header">
            <h2>Your Magic Link (Permanent)</h2>
            <span className="lifesaver-link-note">This link never changes</span>
          </div>
          <p className="lifesaver-description">
            Share this link with all your guests. It will always be the same, even if you modify the form fields.
          </p>
          
          {formUrl && (
            <div className="lifesaver-link-display">
              <div className="lifesaver-link-box">
                <div className="lifesaver-link-url">{formUrl}</div>
                <button
                  className={`lifesaver-copy-btn-large ${copied ? 'copied' : ''}`}
                  onClick={handleCopyLink}
                >
                  {copied ? '✓ Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
          )}

          <div className="lifesaver-link-actions">
            <button className="lifesaver-preview-btn" onClick={handlePreview}>
              Preview as Guest
            </button>
          </div>
        </div>

        {/* Section 5: Reminder and Save Button */}
        <div className="lifesaver-reminder-section">
          {hasChanges && (
            <div className="lifesaver-reminder-box">
              <span className="lifesaver-reminder-icon">💡</span>
              <span>
                Remember: After modifying the fields or changing the status, click on "Save Changes" to apply the modifications.
              </span>
            </div>
          )}
          <button
            className={`lifesaver-save-btn-large ${!hasChanges ? 'lifesaver-save-btn-disabled' : ''}`}
            onClick={(e) => {
              console.log('🔴 BOTÓN CLICKEADO - Save Changes')
              console.log('🔴 hasChanges:', hasChanges)
              console.log('🔴 saving:', saving)
              console.log('🔴 disabled:', !hasChanges || saving)
              if (!hasChanges || saving) {
                console.warn('⚠️ Botón deshabilitado, no se ejecutará handleSaveChanges')
                return
              }
              handleSaveChanges()
            }}
            disabled={!hasChanges || saving}
          >
            <span>✓</span> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

      </div>
    </div>
  )
}

export default TheLifesaverLink
